import { makeAutoObservable } from 'mobx';
import { validateLength } from 'src/api/utils/form2Validators';
import { BonusCreationPatchType, BonusCreationStatusType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { Common } from 'src/AppState/Common';
import { BonusCreationStore } from 'src/domains/casino/state/AppCasino/store/bonus-creation-store/BonusCreation.store';
import { BonusCreationModel } from 'src/domains/casino/state/AppCasino/store/bonus-creation-store/BonusCreationModel';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';

export type GeneralDetailsFormType = {
    nameForm: string;
    termsForm: string;
};

const NO_EMPTY_VALIDATION_MESSAGE = 'Required field';

export class GeneralDetailsState {
    public readonly nameForm: FormInputState<string, string>;
    public readonly termsAndConditionsForm: FormInputState<string, string>;
    private readonly form: FormModel<GeneralDetailsFormType>;
    public bonusCreationStatus: BonusCreationStatusType | null = null;
    private readonly bonusCreationStore: BonusCreationStore;

    public constructor(
        private common: Common,
        private bonusCreationId: string | undefined
    ) {
        makeAutoObservable(this);
        this.bonusCreationStore = BonusCreationStore.get(this.common);

        this.nameForm = FormInputState.new('')
            .map(validateLength(100))
            .map(validateNotEmpty(NO_EMPTY_VALIDATION_MESSAGE));

        this.termsAndConditionsForm = FormInputState.new('').map(validateNotEmpty(NO_EMPTY_VALIDATION_MESSAGE));

        this.form = FormModel.group({
            nameForm: this.nameForm,
            termsForm: this.termsAndConditionsForm,
        });
    }

    public get bonusRewardDisplayName(): string {
        return this.nameForm.value.length > 0 ? this.nameForm.value : 'Untitled';
    }

    public get name(): BonusCreationPatchType['name'] {
        if (this.nameForm.value === '') {
            return undefined;
        }

        return this.nameForm.value;
    }

    public get termsAndConditions(): BonusCreationPatchType['terms'] {
        if (this.termsAndConditionsForm.value === '') {
            return undefined;
        }
        return this.termsAndConditionsForm.value;
    }

    public validNotEmpty(): void {
        this.form.mapChild((item) => {
            if (item instanceof FormInputState) {
                const value = item.value;
                if (value.trim() !== '') {
                    item.setAsVisited();
                }
            }

            return item;
        });
    }

    public isTouched(): boolean {
        return this.form.isTouched();
    }

    public setIsTouched(): void {
        this.form.setIsTouched();
    }

    public resetIsTouched(): void {
        this.form.resetIsTouched();
    }

    public resetIsVisited(): void {
        this.form.resetIsVisited();
    }

    public validateForm(): boolean {
        this.form.setAsVisited();
        return this.form.result.value.type === 'ok';
    }

    public get formErrorsCount(): number {
        return this.form.errorsForView.length;
    }

    public setFormValues = async (bonusRewardModel: BonusCreationModel): Promise<void> => {
        this.bonusCreationStatus = bonusRewardModel.status;

        this.nameForm.setValue(bonusRewardModel.name);
        this.termsAndConditionsForm.setValue(bonusRewardModel.terms);
    };

    public get bonusReward(): BonusCreationModel | null {
        if (this.bonusCreationId === undefined) {
            return null;
        }
        return this.bonusCreationStore.getBonusCreation(this.bonusCreationId);
    }
}
